import { useEffect, useState } from 'react';
import type { FinancialOverview } from '@tosho/contracts';
import { financialOverviewApi } from './financial-overview-api';
import { ManualBankBalanceDialog } from './ManualBankBalanceDialog';

/** The manual fallback is an organization total, independent of Freebe's account count.
 * Reading it never marks an automatic acquisition successful or creates a transaction. */
export function FreebeBankBalanceFallback({
  csrfToken,
  disabled,
  synchronizedAt,
}: {
  readonly csrfToken: string;
  readonly disabled: boolean;
  readonly synchronizedAt: string | null;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [revision, setRevision] = useState(0);
  const [balance, setBalance] = useState<FinancialOverview['bankBalance'] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    const month = new Intl.DateTimeFormat('fr-CA', {
      timeZone: 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
    }).format(new Date());
    void financialOverviewApi
      .read(month)
      .then((overview) => {
        if (active) {
          setBalance(overview.bankBalance);
          setUnavailable(false);
        }
      })
      .catch(() => {
        if (active) {
          setBalance(null);
          setUnavailable(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [revision, synchronizedAt]);
  return (
    <span className="freebe-integration__bank-fallback">
      <small aria-live="polite">
        {loading
          ? 'Lecture du solde courant…'
          : balance?.cents != null
            ? `${balance.source === 'MANUAL' ? 'Saisie manuelle' : balance.source === 'FREEBE' ? 'Solde Freebe' : 'Solde daté'} : ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(balance.cents) / 100)} · ${new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(balance.observedAt ?? balance.date!))}`
            : unavailable
              ? 'Lecture du solde courant indisponible.'
              : 'Aucun solde daté disponible.'}
      </small>
      {saved ? (
        <small role="status">
          Solde manuel enregistré. Il sera utilisé tant qu’aucun relevé Freebe au moins aussi récent
          n’est disponible.
        </small>
      ) : null}
      <button
        type="button"
        className="is-secondary"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        Saisir un solde manuel
      </button>
      {open ? (
        <ManualBankBalanceDialog
          csrfToken={csrfToken}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            setSaved(true);
            setRevision((value) => value + 1);
          }}
        />
      ) : null}
    </span>
  );
}
